import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type UIEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { usePlacesAutocomplete, type PlacesSelection } from '@/hooks/usePlacesAutocomplete';
import { RecaptchaNotice } from '@/components/RecaptchaNotice';
import { WCta } from '../WCta';
import { WregPhase } from '../WregPhase';
import { WREG_WORKING_MS } from '../wregSteps';
import {
  COMPLETE_SHIPPING_FIELD,
  SCROLL_SHIPPING_DETAILS,
  SELECT_ADDRESS_SUGGESTION,
  SELECT_SHIPPING_FIELD,
  SUBMIT_SHIPPING_DETAILS,
} from '@/consts/events';
import {
  useWregScrollDirection,
  type WregScreenEvent,
  type WregScrollDirection,
} from './wregScreenEvents';
import {
  EMPTY_SHIPPING,
  normalizeZip,
  shippingIsComplete,
  type ShippingForm,
} from './wregShippingFields';

type Props = {
  initial?: ShippingForm;
  onContinue: (form: ShippingForm) => void | Promise<void>;
  onContinued?: () => void;
  onEvent?: WregScreenEvent;
};

type ShippingErrorKey = Exclude<keyof ShippingForm, 'apt'>;
type ShippingErrors = Partial<Record<ShippingErrorKey, string>>;

const SHIPPING_FIELD_ORDER: ShippingErrorKey[] = ['first', 'last', 'street', 'city', 'state', 'zip'];
const REQUIRED_FIELD_MESSAGE = 'This field is required.';
const INVALID_ZIP_MESSAGE = 'Enter a valid 5-digit ZIP code.';
const errorId = (field: ShippingErrorKey) => `wreg-mon-${field}-err`;

const FIELD_NAME: Record<keyof ShippingForm, string> = {
  first: 'first_name',
  last: 'last_name',
  street: 'street',
  apt: 'apt',
  city: 'city',
  state: 'state',
  zip: 'zip',
};

const WregMonitor = ({ initial, onContinue, onContinued, onEvent }: Props) => {
  const { t } = useTranslation();
  const [f, setF] = useState<ShippingForm>(() => initial ?? EMPTY_SHIPPING);
  const [touched, setTouched] = useState<Set<ShippingErrorKey>>(() => new Set());
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [bfade, setBfade] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const streetRef = useRef<HTMLInputElement>(null);

  const fadeFor = (el: HTMLElement) => el.scrollTop + el.clientHeight < el.scrollHeight - 4;
  const onScrollDirection = useCallback(
    (direction: WregScrollDirection) => onEvent?.(SCROLL_SHIPPING_DETAILS, { direction }),
    [onEvent],
  );
  const trackScroll = useWregScrollDirection(onScrollDirection);
  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    setBfade(fadeFor(e.currentTarget));
    trackScroll(e.currentTarget);
  };

  useEffect(() => {
    const el = bodyRef.current;
    if (el) setBfade(fadeFor(el));
  }, []);

  const set = (k: keyof ShippingForm) => (e: ChangeEvent<HTMLInputElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));
  const touch = (field: ShippingErrorKey) => () =>
    setTouched((previous) => new Set(previous).add(field));

  const setZip = (e: ChangeEvent<HTMLInputElement>) =>
    setF((p) => ({ ...p, zip: normalizeZip(e.target.value) }));

  const applyPlace = useCallback((place: PlacesSelection) => {
    onEvent?.(SELECT_ADDRESS_SUGGESTION, {
      filled_fields: (['street', 'city', 'state', 'zip'] as const).filter((field) => place[field]),
    });
    setF((p) => ({
      ...p,
      street: place.street || p.street,
      city: place.city || p.city,
      state: place.state || p.state,
      zip: place.zip ? normalizeZip(place.zip) : p.zip,
    }));
  }, [onEvent]);

  const { enabled: placesEnabled } = usePlacesAutocomplete({
    inputRef: streetRef,
    onSelect: applyPlace,
    scrollRef: bodyRef,
    placeholder: t('wreg.mon.street.ph'),
  });

  const valid = shippingIsComplete(f);
  const errors = useMemo<ShippingErrors>(() => {
    const next: ShippingErrors = {};
    if (!f.first.trim()) next.first = REQUIRED_FIELD_MESSAGE;
    if (!f.last.trim()) next.last = REQUIRED_FIELD_MESSAGE;
    if (!f.street.trim()) next.street = REQUIRED_FIELD_MESSAGE;
    if (!f.city.trim()) next.city = REQUIRED_FIELD_MESSAGE;
    if (!f.state.trim()) next.state = REQUIRED_FIELD_MESSAGE;
    if (!/^\d{5}$/.test(f.zip)) next.zip = INVALID_ZIP_MESSAGE;
    return next;
  }, [f]);
  const focus = (field: keyof ShippingForm) => () =>
    onEvent?.(SELECT_SHIPPING_FIELD, { field: FIELD_NAME[field] });
  const blur = (field: keyof ShippingForm) => () => {
    if (field !== 'apt') touch(field)();
    if (!f[field].trim()) return;
    const isValid = field === 'apt' || !errors[field];
    onEvent?.(COMPLETE_SHIPPING_FIELD, {
      field: FIELD_NAME[field],
      is_valid: isValid,
      ...(isValid ? {} : { invalidity_reason: 'format' }),
    });
  };
  const showError = (field: ShippingErrorKey) =>
    Boolean(errors[field]) && (submitAttempted || touched.has(field));
  const validateBeforeSubmit = () => {
    if (valid) return true;
    setSubmitAttempted(true);
    onEvent?.(SUBMIT_SHIPPING_DETAILS, {
      is_valid: false,
      invalid_fields: SHIPPING_FIELD_ORDER.filter((field) => errors[field]).map(
        (field) => FIELD_NAME[field],
      ),
    });
    const firstInvalid = SHIPPING_FIELD_ORDER.find((field) => errors[field]);
    if (firstInvalid) {
      requestAnimationFrame(() => {
        const control = document.getElementById(`wreg-mon-${firstInvalid}`);
        control?.focus();
        control?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      });
    }
    return false;
  };

  return (
    <div className="rg-screen">
      <div className="rg-body-wrap">
        <div className="rg-body" ref={bodyRef} onScroll={onScroll}>
          <div className="rg-inner">
            <div className="rg-head-block">
              <WregPhase n={4} />
              <h1 className="rg-h1">{t('wreg.mon.title')}</h1>
              <p className="rg-sub">{t('wreg.mon.sub')}</p>
            </div>
            <div className="rg-ship-head">{t('wreg.mon.ship_head')}</div>
            <div className="rg-field-row">
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-mon-first">
                  {t('wreg.intake.first')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-mon-first"
                    className="rg-input"
                    autoComplete="given-name"
                    value={f.first}
                    onChange={set('first')}
                    onFocus={focus('first')}
                    onBlur={blur('first')}
                    aria-invalid={showError('first') || undefined}
                    aria-describedby={showError('first') ? errorId('first') : undefined}
                  />
                </div>
                {showError('first') && (
                  <span className="rg-error-hint" id={errorId('first')}>{errors.first}</span>
                )}
              </div>
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-mon-last">
                  {t('wreg.intake.last')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-mon-last"
                    className="rg-input"
                    autoComplete="family-name"
                    value={f.last}
                    onChange={set('last')}
                    onFocus={focus('last')}
                    onBlur={blur('last')}
                    aria-invalid={showError('last') || undefined}
                    aria-describedby={showError('last') ? errorId('last') : undefined}
                  />
                </div>
                {showError('last') && (
                  <span className="rg-error-hint" id={errorId('last')}>{errors.last}</span>
                )}
              </div>
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-mon-street">
                {t('wreg.mon.street.label')}
              </label>
              <div className="rg-input-wrap">
                <input
                  id="wreg-mon-street"
                  ref={streetRef}
                  className="rg-input"
                  value={f.street}
                  onChange={set('street')}
                  onFocus={focus('street')}
                    onBlur={blur('street')}
                  placeholder={t('wreg.mon.street.ph')}
                  autoComplete={placesEnabled ? 'off' : 'address-line1'}
                  aria-invalid={showError('street') || undefined}
                  aria-describedby={showError('street') ? errorId('street') : undefined}
                />
              </div>
              {showError('street') && (
                <span className="rg-error-hint" id={errorId('street')}>{errors.street}</span>
              )}
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-mon-apt">
                {t('wreg.mon.apt.label')}{' '}
                <span className="rg-optional">{t('wreg.mon.optional')}</span>
              </label>
              <div className="rg-input-wrap">
                <input
                  id="wreg-mon-apt"
                  className="rg-input"
                  autoComplete="address-line2"
                  value={f.apt}
                  onChange={set('apt')}
                  onFocus={focus('apt')}
                  onBlur={blur('apt')}
                />
              </div>
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-mon-city">
                {t('wreg.mon.city')}
              </label>
              <div className="rg-input-wrap">
                <input
                  id="wreg-mon-city"
                  className="rg-input"
                  autoComplete="address-level2"
                  value={f.city}
                  onChange={set('city')}
                  onFocus={focus('city')}
                    onBlur={blur('city')}
                  aria-invalid={showError('city') || undefined}
                  aria-describedby={showError('city') ? errorId('city') : undefined}
                />
              </div>
              {showError('city') && (
                <span className="rg-error-hint" id={errorId('city')}>{errors.city}</span>
              )}
            </div>
            <div className="rg-field-row">
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-mon-state">
                  {t('wreg.mon.state')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-mon-state"
                    className="rg-input"
                    autoComplete="address-level1"
                    value={f.state}
                    onChange={set('state')}
                    onFocus={focus('state')}
                    onBlur={blur('state')}
                    aria-invalid={showError('state') || undefined}
                    aria-describedby={showError('state') ? errorId('state') : undefined}
                  />
                </div>
                {showError('state') && (
                  <span className="rg-error-hint" id={errorId('state')}>{errors.state}</span>
                )}
              </div>
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-mon-zip">
                  {t('wreg.mon.zip')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-mon-zip"
                    className="rg-input"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    value={f.zip}
                    onChange={setZip}
                    onFocus={focus('zip')}
                    onBlur={blur('zip')}
                    aria-invalid={showError('zip') || undefined}
                    aria-describedby={showError('zip') ? errorId('zip') : undefined}
                  />
                </div>
                {showError('zip') && (
                  <span className="rg-error-hint" id={errorId('zip')}>{errors.zip}</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={`rg-list-fade${bfade ? ' show' : ''}`} aria-hidden="true" />
      </div>
      <div className="rg-foot">
        <div className="wreg-foot-row">
          <WCta
            beforeClick={validateBeforeSubmit}
            dimmed={!valid}
            working={t('wreg.mon.cta_loading')}
            minWorkingMs={WREG_WORKING_MS}
            onClick={() => onContinue(f)}
            onDone={onContinued}
          >
            {t('wreg.mon.cta')}
          </WCta>
          {!valid && <span className="wreg-foot-tip">{t('wreg.foot.fillall')}</span>}
        </div>
        <div className="wreg-foot-legal">
          <RecaptchaNotice />
        </div>
      </div>
    </div>
  );
};

export { WregMonitor };
